import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";
import type { CoreToggle } from "./coreToggle";

export class CoreInputSelect implements Plugin {
  private input: HTMLInputElement | null;
  private clickHandler: (e: MouseEvent) => void;
  private clickOutsideHandler: (e: MouseEvent) => void;
  private toggleChangeHandler: (e: Event) => void;
  private togglePlugin: CoreToggle | null = null;
  private triggerElement: HTMLElement | null;
  private dropdown: HTMLElement | null;

  constructor(private readonly element: HTMLElement) {
    this.input = this.element.querySelector("input.core-inputText__input");
    this.triggerElement = this.element.querySelector(
      ".core-inputText__container",
    );
    this.dropdown = this.element.querySelector(".core-inputSelect__dropdown");

    this.clickHandler = (e: MouseEvent) => this.handleDropdownClick(e);
    this.clickOutsideHandler = (e: MouseEvent) => {
      if (
        this.togglePlugin?.isOpen() &&
        !this.element.contains(e.target as Node)
      ) {
        this.togglePlugin.close();
      }
    };
    this.toggleChangeHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.element.classList.toggle("is-open", detail?.open === true);
    };
  }

  init(): void {
    const isDisabled =
      this.element.classList.contains("core-inputSelect--disabled") ||
      this.input?.disabled === true;

    if (isDisabled) return;

    setTimeout(() => {
      const toggleElement = this.element.querySelector(
        ".core-inputSelect__toggle-wrapper",
      ) as HTMLElement;
      if (toggleElement) {
        this.togglePlugin = PluginRegistry.getInstance(
          toggleElement,
        ) as CoreToggle;
      }
    }, 0);

    if (this.dropdown) {
      this.dropdown.addEventListener("click", this.clickHandler);
    }

    document.addEventListener("click", this.clickOutsideHandler);

    this.element.addEventListener(
      "coreToggle:change",
      this.toggleChangeHandler,
    );

    if (this.input) {
      this.input.readOnly = true;
      this.input.style.cursor = "pointer";
    }

    this.input?.addEventListener("click", (e) => {
      e.preventDefault();
    });
  }

  private handleDropdownClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const item = target.closest("li, [data-value]");

    if (!item) return;

    const value =
      item.getAttribute("data-value") || item.textContent?.trim() || "";
    const label =
      item.getAttribute("data-label") || item.textContent?.trim() || "";

    if (this.input) {
      this.input.value = label;
      this.input.dataset.value = value;
    }

    const hiddenInputId = `${this.input?.id || "select"}-hidden-value`;
    let hiddenInput = this.element.querySelector(
      `#${hiddenInputId}`,
    ) as HTMLInputElement;

    if (!hiddenInput) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.id = hiddenInputId;
      if (this.element.dataset.name) {
        hiddenInput.name = this.element.dataset.name;
      }
      this.element.appendChild(hiddenInput);
    }

    hiddenInput.value = value;

    if (this.togglePlugin) {
      this.input?.focus();
      this.togglePlugin.close();
    }

    this.element.dispatchEvent(
      new CustomEvent("coreInputSelect:change", {
        bubbles: true,
        detail: {
          value: value,
          label: label,
        },
      }),
    );
  }

  destroy(): void {
    if (this.dropdown) {
      this.dropdown.removeEventListener("click", this.clickHandler);
    }
    document.removeEventListener("click", this.clickOutsideHandler);
    this.element.removeEventListener(
      "coreToggle:change",
      this.toggleChangeHandler,
    );
    this.element.classList.remove("is-open");
  }
}

PluginRegistry.register("coreInputSelect", CoreInputSelect);
